/**
 * Turn a stored answer into the text a person should get when they copy it.
 *
 * The rendered answer and the stored answer are not the same string. The model
 * cites as `[docN]` (as instructed by retrieval, see cn_ai_lib's AzureLLMClient), and only the render resolves those:
 * `remarkCitations` turns them into `citation-N` links and `Message.tsx` prints
 * `[display_index]`, or nothing at all when a marker has no citation behind it.
 * The stored text keeps the raw markers.
 *
 * So copying `content.text` verbatim hands over `[doc1]`, which is exactly what
 * reached us in Danny's reports (MA-237): `[doc1]`/`[doc2]` pasted into Teams and
 * `[doc2]`-`[doc5]` into a Word document, while his screenshots of the same
 * answers showed a clean `[1]`. What you see was right; what you pasted was not.
 *
 * This produces the seen form, plus a source list, so a pasted answer stands on
 * its own in a ticket or an e-mail.
 */
import type { Citation } from "@/lib/api/types";

/** Same pattern remarkCitations matches, so the two cannot drift apart. */
const MARKER_RE = /\[doc(\d+)\]/g;

/**
 * Split text into code and non-code runs.
 *
 * Markers inside a fenced block or inline backticks are content the reader is
 * meant to see literally, and the render leaves them alone: `remarkCitations`
 * walks the parsed tree precisely so that a `code`/`inlineCode` node is skipped
 * structurally. Its own comment records that an earlier string-level regex
 * "happily link-ified markers the user was meant to read literally".
 *
 * Rewriting on a flat string would repeat that bug. Re-parsing to mdast and
 * serialising back would avoid it but reformat the whole answer, which is a worse
 * outcome for a copy than a stray marker. Tokenising the code runs is the middle
 * path: fences first, so a backtick inside a fenced block cannot open an inline
 * span.
 */
function splitOnCode(text: string): { code: boolean; value: string }[] {
  const parts: { code: boolean; value: string }[] = [];
  // Fenced blocks (``` … ```) and inline spans (` … `), fences first.
  const CODE_RE = /(```[\s\S]*?```|`[^`\n]*`)/g;
  let cursor = 0;
  let match: RegExpExecArray | null;
  while ((match = CODE_RE.exec(text)) !== null) {
    if (match.index > cursor) {
      parts.push({ code: false, value: text.slice(cursor, match.index) });
    }
    parts.push({ code: true, value: match[0] });
    cursor = match.index + match[0].length;
  }
  if (cursor < text.length) {
    parts.push({ code: false, value: text.slice(cursor) });
  }
  return parts;
}

/** marker number -> the citation it points at. */
function byMarker(citations: Citation[]): Map<number, Citation> {
  const map = new Map<number, Citation>();
  for (const citation of citations) {
    for (const marker of citation.markers ?? []) map.set(marker, citation);
  }
  return map;
}

/**
 * The answer as the reader saw it, with its sources appended.
 *
 * Resolvable markers become `[display_index]`. Unresolvable ones are dropped,
 * matching the render, which returns null rather than showing `[doc7]` for a
 * marker the model invented.
 *
 * Sources are listed in `display_index` order and only when there are any: an
 * out-of-scope reply cites nothing, and appending an empty heading to it would be
 * noise.
 */
export default function answerForCopy(
  text: string,
  citations: Citation[] = [],
): string {
  const markers = byMarker(citations);

  const body = splitOnCode(text)
    .map((part) =>
      part.code
        ? part.value
        : part.value.replace(MARKER_RE, (_whole, digits: string) => {
            const citation = markers.get(parseInt(digits, 10));
            return citation ? `[${citation.display_index}]` : "";
          }),
    )
    .join("");

  if (citations.length === 0) return body;

  const sources = [...citations]
    .sort((a, b) => a.display_index - b.display_index)
    .map((c) => `[${c.display_index}] ${c.title}`)
    .join("\n");

  // "Sources" mirrors the label on screen (Message.tsx:409) so the pasted text
  // reads like what the person was looking at. Note that label is English while
  // the rest of the user-facing copy is Dutch (MA-204) -- a separate
  // inconsistency, and changing visible wording is the client's call, not a
  // side effect of a clipboard fix.
  return `${body}\n\nSources:\n${sources}`;
}
